// InvoiceGeneralInfo.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Utilities
import { coerceToDateTimeRangeValue } from 'utilities/DateTime';
import { buildNodeFieldPatch } from 'utilities/NodeFieldData';
import { getResolvedFields } from 'utilities/NodeType';
import { formatPurchaseOrderDate } from 'nodetypes/purchaseorder/view/purchaseOrderGeneralInfoModel';
import {
  INVOICE_TYPE_KEY,
  readInvoiceField,
} from 'nodetypes/invoice/view/invoiceGeneralInfoModel';

// CSS
import 'nodetypes/purchaseorder/view/PurchaseOrderGeneralInfo.css';

// Components
import TextInput from 'components/textinput/TextInput';
import NodeDateInput from 'node/form/fields/date/NodeDateInput';
import DocumentLetterhead from 'nodetypes/documents/kit/DocumentLetterhead';
import InvoiceClientParty from 'nodetypes/invoice/view/InvoiceClientParty';

const INVOICE_DATE_FIELDS = Object.freeze([
  { name: 'sendDate', label: 'Date' },
  { name: 'dueDate', label: 'Due Date' },
]);

const INVOICE_DATE_FRIENDLY_FORMAT = Object.freeze({
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

function getInvoiceSchemaField(nodeType, fieldName) {
  const name = String(fieldName || '').trim();
  if (!name) return null;
  const fields = Array.isArray(nodeType?.schema?.fields)
    ? nodeType.schema.fields
    : (getResolvedFields(nodeType?.key || INVOICE_TYPE_KEY, { includeHidden: true }) || []);
  return fields.find((field) => String(field?.name || '').trim() === name) || null;
}

function DisplayValue({ value, emptyLabel = '—' }) {
  const cleaned = String(value || '').trim();
  return (
    <div className={`po-general-info-value${cleaned ? '' : ' is-empty'}`}>
      {cleaned || emptyLabel}
    </div>
  );
}

/**
 * InvoiceGeneralInfo
 */
const InvoiceGeneralInfo = React.memo(({
  node,
  nodeType = null,
  readOnly = false,
  onNodeChange,
  letterheadOverride = null,
}) => {
  const canEdit = Boolean(!readOnly && typeof onNodeChange === 'function' && node?.key);
  const typeKey = String(nodeType?.key || node?.type || INVOICE_TYPE_KEY).trim().toUpperCase();
  const nodeTitle = String(node?.title || '');
  const nodeNumber = useMemo(() => {
    const raw = readInvoiceField(node, 'number', nodeType);
    return raw == null ? '' : String(raw).trim();
  }, [node, nodeType]);

  const [titleDraft, setTitleDraft] = useState(nodeTitle);
  const [numberDraft, setNumberDraft] = useState(nodeNumber);

  useEffect(() => {
    setTitleDraft(nodeTitle);
  }, [node?.key, nodeTitle]);

  useEffect(() => {
    setNumberDraft(nodeNumber);
  }, [node?.key, nodeNumber]);

  const dateFieldValues = useMemo(() => {
    const next = {};
    INVOICE_DATE_FIELDS.forEach(({ name }) => {
      next[name] = coerceToDateTimeRangeValue(
        readInvoiceField(node, name, nodeType),
      );
    });
    return next;
  }, [node, nodeType]);

  const handleTitleBlur = useCallback(() => {
    if (!canEdit || !node?.key) return;
    const next = String(titleDraft || '').trim();
    if (next === String(node.title || '').trim()) return;
    onNodeChange(node.key, { title: next });
  }, [canEdit, node, onNodeChange, titleDraft]);

  const handleNumberBlur = useCallback(() => {
    if (!canEdit || !node?.key) return;
    const next = String(numberDraft || '').trim();
    const current = String(readInvoiceField(node, 'number', nodeType) || '').trim();
    if (next === current) return;
    const schemaField = getInvoiceSchemaField(nodeType, 'number');
    const field = schemaField || {
      name: 'number',
      source: typeKey.toLowerCase(),
      type: 'sequence',
    };
    const patch = buildNodeFieldPatch(node, field, typeKey, next);
    if (!patch) return;
    onNodeChange(node.key, patch);
  }, [canEdit, node, nodeType, numberDraft, onNodeChange, typeKey]);

  const handleDateFieldChange = useCallback((fieldName) => (nextValue) => {
    if (!canEdit) return;
    const schemaField = getInvoiceSchemaField(nodeType, fieldName);
    const field = schemaField || {
      name: fieldName,
      source: typeKey.toLowerCase(),
      type: 'datetime',
    };
    const patch = buildNodeFieldPatch(node, field, typeKey, nextValue);
    if (!patch) return;
    onNodeChange(node.key, patch);
  }, [canEdit, node, nodeType, onNodeChange, typeKey]);

  return (
    <div className="po-general-info">
      <div className="po-general-info-parties">
        <div className="po-general-info-party">
          <DocumentLetterhead letterheadOverride={letterheadOverride} />
        </div>
        <InvoiceClientParty
          node={node}
          nodeType={nodeType}
          readOnly={readOnly}
          onNodeChange={onNodeChange}
        />
      </div>

      <div className="po-general-info-meta">
        <div className="po-general-info-title-block">
          {canEdit ? (
            <div className="po-general-info-title-input">
              <TextInput
                value={titleDraft}
                placeholder="Add a title…"
                hybrid
                autoFocus={false}
                autoSelect={false}
                width="100%"
                style={{
                  fontSize: '22px',
                  fontWeight: 700,
                  lineHeight: 1.3,
                  paddingLeft: 10,
                  paddingRight: 8,
                  paddingTop: 2,
                  paddingBottom: 2,
                }}
                onChange={(event) => setTitleDraft(event?.target?.value ?? '')}
                onBlur={handleTitleBlur}
              />
            </div>
          ) : (
            <div className="po-general-info-title">{nodeTitle || '—'}</div>
          )}
        </div>

        <div className="po-general-info-fields">
          <div className="po-general-info-field">
            <div className="po-general-info-label">Invoice #</div>
            {canEdit ? (
              <TextInput
                value={numberDraft}
                placeholder="INV-…"
                hybrid
                width="100%"
                onChange={(event) => setNumberDraft(event?.target?.value ?? '')}
                onBlur={handleNumberBlur}
              />
            ) : (
              <DisplayValue value={nodeNumber} emptyLabel="—" />
            )}
          </div>

          {INVOICE_DATE_FIELDS.map(({ name, label }) => (
            <div className="po-general-info-field" key={name}>
              <div className="po-general-info-label">{label}</div>
              {canEdit ? (
                <div className="po-general-info-field-date">
                  <NodeDateInput
                    value={dateFieldValues[name]}
                    hybrid
                    iconSize={18}
                    friendlyFormatOptions={INVOICE_DATE_FRIENDLY_FORMAT}
                    friendlyFontWeight={700}
                    onChange={handleDateFieldChange(name)}
                  />
                </div>
              ) : (
                <DisplayValue
                  value={formatPurchaseOrderDate(readInvoiceField(node, name, nodeType))}
                  emptyLabel="—"
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

InvoiceGeneralInfo.displayName = 'InvoiceGeneralInfo';

export default InvoiceGeneralInfo;
